import React, { Fragment, FC, useEffect, memo } from 'react';
import { Box, Text, Icon, NakedButton } from 'roo-ui/components';
import { Drawer, DrawerClose, MenuBtn, PhoneMenuLink } from './primitives';
import { QantasUniversalNav } from '../../QantasUniversalNav';
import { QffAuthDetails } from './QffAuthDetails';
import {
  HeaderContextType,
  InteractionInfo,
  useHeaderContext,
} from '../../HeaderContext';
import { fixBodyScroll, unfixBodyScroll } from '../../../../lib/browser';

interface PhoneMenuProps {
  qffLoginEnabled?: boolean;
  universalNavEnabled?: boolean;
}

export const PhoneMenu: FC<PhoneMenuProps> = ({
  qffLoginEnabled = true,
  universalNavEnabled = true,
}) => {
  const { phoneLinks, isNavMenuOpen, toggleNavMenu, onInteraction } =
    useHeaderContext();

  useEffect(() => {
    if (isNavMenuOpen) {
      fixBodyScroll();
    } else {
      unfixBodyScroll();
    }

    return () => unfixBodyScroll();
  }, [isNavMenuOpen]);

  const handleToggleNavMenu = () => {
    onInteraction({
      type: 'Mobile Menu',
      value: isNavMenuOpen ? 'Menu Collapsed' : 'Menu Expanded',
    });
    toggleNavMenu();
  };

  const handleNavInteraction = (interaction: InteractionInfo) => {
    onInteraction(interaction);
    toggleNavMenu();
  };

  return (
    <Fragment>
      <MenuBtn
        role="button"
        isOpen={isNavMenuOpen}
        onClick={handleToggleNavMenu}
        data-testid="menu-button"
      >
        <Text fontSize="header.base">Menu</Text>
      </MenuBtn>

      <Drawer isOpen={isNavMenuOpen}>
        {qffLoginEnabled && (
          <Box p={4} borderColor="white" borderBottom={1}>
            <QffAuthDetails />
          </Box>
        )}

        <PhoneLinks
          phoneLinks={phoneLinks}
          onInteraction={handleNavInteraction}
        />

        {universalNavEnabled && <QantasUniversalNav variant="mobile" />}
      </Drawer>

      <DrawerClose data-testid="close-btn-wrapper" isOpen={isNavMenuOpen}>
        <NakedButton
          role="button"
          onClick={handleToggleNavMenu}
          data-testid="close-btn"
        >
          <Icon name="close" mt={7} />
        </NakedButton>
      </DrawerClose>
    </Fragment>
  );
};

type PhoneLinksProps = Pick<HeaderContextType, 'phoneLinks' | 'onInteraction'>;

const PhoneLinks = memo<PhoneLinksProps>(({ phoneLinks, onInteraction }) => (
  <>
    {phoneLinks.map(({ trackingName, ...link }) => (
      <PhoneMenuLink
        key={trackingName}
        onClick={() =>
          onInteraction({
            type: 'Site Navigation',
            value: `${trackingName} Selected`,
          })
        }
        {...link}
      />
    ))}
  </>
));
